
import { GenerateJigProgress } from '@/services/GenerateJigService'
import { Component, Vue } from 'vue-property-decorator'

type LottieAnimationInstance = {
  destroy: () => void
}

type LottieAnimationConfig = {
  container: HTMLElement
  renderer: string
  loop: boolean
  autoplay: boolean
  path: string
}

type LottieGlobal = {
  loadAnimation: (config: LottieAnimationConfig) => LottieAnimationInstance
}

@Component
export default class GenerateJigLoadingSection extends Vue {
  private loadingMessage: string = 'Preparing Jig generation...'
  private loadingPercentage: number = 0
  private loadingProgressAnimationFrame: number | null = null
  private displayedLoadingPercentage: number = 0
  private lottieAnimation: LottieAnimationInstance | null = null

  private mounted() {
    this.startLottieAnimation()
  }

  private beforeDestroy() {
    this.stopLottieAnimation()
  }

  public updateProgress(progress: GenerateJigProgress) {
    this.loadingMessage = progress && progress.message ? progress.message : this.loadingMessage
    this.animateLoadingPercentageTo(progress && typeof progress.percentage === 'number' ? progress.percentage : this.loadingPercentage)
  }

  private getLottieContainer(): HTMLElement | null {
    const refs = this.$refs as { lottieContainer?: HTMLElement | HTMLElement[] }
    const containerRef = refs.lottieContainer

    if (!containerRef) {
      return null
    }

    if (Array.isArray(containerRef)) {
      if (containerRef.length === 0) {
        return null
      }
      return containerRef[0]
    }

    return containerRef
  }

  private clampPercentage(value: number): number {
    if (value < 0) {
      return 0
    }
    if (value > 100) {
      return 100
    }
    return Math.round(value)
  }

  private stopLoadingProgressAnimation() {
    if (this.loadingProgressAnimationFrame !== null) {
      window.cancelAnimationFrame(this.loadingProgressAnimationFrame)
      this.loadingProgressAnimationFrame = null
    }
  }

  private animateLoadingPercentageTo(targetPercentage: number) {
    const nextTarget = this.clampPercentage(targetPercentage)
    this.stopLoadingProgressAnimation()

    const startValue = this.displayedLoadingPercentage
    const delta = nextTarget - startValue

    if (delta === 0) {
      this.loadingPercentage = nextTarget
      this.displayedLoadingPercentage = nextTarget
      return
    }

    if (delta < 0) {
      this.loadingPercentage = nextTarget
      this.displayedLoadingPercentage = nextTarget
      return
    }

    const duration = delta > 20 ? 350 : 220
    const startTime = Date.now()

    const animate = () => {
      const elapsed = Date.now() - startTime
      const progress = Math.min(elapsed / duration, 1)
      const eased = 1 - Math.pow(1 - progress, 3)
      const value = startValue + delta * eased
      const roundedValue = this.clampPercentage(value)

      this.displayedLoadingPercentage = value
      this.loadingPercentage = roundedValue

      if (progress < 1) {
        this.loadingProgressAnimationFrame = window.requestAnimationFrame(animate)
        return
      }

      this.loadingProgressAnimationFrame = null
      this.displayedLoadingPercentage = nextTarget
      this.loadingPercentage = nextTarget
    }

    this.loadingProgressAnimationFrame = window.requestAnimationFrame(animate)
  }

  private startLottieAnimation() {
    const lottieHost = window as unknown as { lottie?: LottieGlobal }
    const container = this.getLottieContainer()

    if (!container) {
      return
    }

    this.stopLottieAnimation()
    container.innerHTML = ''

    if (!lottieHost.lottie || typeof lottieHost.lottie.loadAnimation !== 'function') {
      return
    }

    this.lottieAnimation = lottieHost.lottie.loadAnimation({
      container: container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: '/img/lottie/loader.json',
    })
  }

  private stopLottieAnimation() {
    const container = this.getLottieContainer()
    this.stopLoadingProgressAnimation()

    if (this.lottieAnimation && typeof this.lottieAnimation.destroy === 'function') {
      this.lottieAnimation.destroy()
    }

    this.lottieAnimation = null

    if (container) {
      container.innerHTML = ''
    }
  }
}
